import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, ArrowRight, CheckCircle, Clock, Shield, Star, MapPin, Users, Award
} from 'lucide-react';
import { companyInfo, aboutContent, cities } from '../../data/mock';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../ui/breadcrumb';
import SEO, { seoData } from '../seo/SEO';

const AboutPage = () => {
  // About page Schema.org structured data
  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "À propos de NGL SERVICES",
    "description": seoData.about.description,
    "mainEntity": {
      "@type": "LocalBusiness",
      "name": companyInfo.name,
      "description": companyInfo.description,
      "telephone": companyInfo.phone,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Villiers-sur-Marne",
        "postalCode": "94350",
        "addressCountry": "FR"
      }
    }
  };

  return (
    <div className="min-h-screen">
      {/* SEO Meta Tags */}
      <SEO 
        title={seoData.about.title}
        description={seoData.about.description}
        keywords={seoData.about.keywords}
        canonical="/a-propos"
        schemaData={aboutSchema}
      />

      {/* Hero Section */}
      <section className="relative py-20 bg-[hsl(207,78%,42%)]">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
          }} />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <Breadcrumb className="mb-6">
            <BreadcrumbList className="text-white/70">
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/" className="hover:text-white">Accueil</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="text-white/50" />
              <BreadcrumbItem>
                <BreadcrumbPage className="text-white">À propos</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {aboutContent.title}
            </h1>
            <p className="text-xl text-white/90">
              {aboutContent.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* About Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="space-y-6 text-[hsl(215,25%,25%)] leading-relaxed">
                {aboutContent.paragraphs.map((paragraph, i) => (
                  <p key={i} className={i === 0 ? "text-lg" : ""}>
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button className="btn-primary rounded-full px-8">
                    Nous contacter
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <a href={`tel:${companyInfo.phone}`}>
                  <Button variant="outline" className="rounded-full px-8">
                    <Phone className="w-4 h-4 mr-2" />
                    {companyInfo.phone}
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/5691503/pexels-photo-5691503.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="NGL SERVICES - Menuisier professionnel"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-[hsl(24,95%,53%)] text-white p-6 rounded-xl shadow-xl hidden md:block">
                <div className="text-3xl font-bold">24h/24</div>
                <div className="text-sm">Urgences disponibles</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-[hsl(210,20%,98%)]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="badge-primary mb-4">Nos valeurs</Badge>
            <h2 className="text-3xl font-bold text-[hsl(215,25%,15%)] mb-4">
              Ce qui nous distingue
            </h2>
            <div className="section-divider" />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {aboutContent.values.map((value, i) => {
              const icons = [Clock, Star, Shield, MapPin];
              const Icon = icons[i];
              return (
                <Card key={i} className="border-0 shadow-md text-center service-card">
                  <CardContent className="p-6">
                    <div className="w-16 h-16 rounded-2xl bg-[hsl(207,78%,95%)] flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-8 h-8 text-[hsl(207,78%,42%)]" />
                    </div>
                    <h3 className="text-xl font-bold text-[hsl(215,25%,15%)] mb-2">
                      {value.title}
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-[hsl(207,78%,42%)]">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
            {[
              { value: "24h/24", label: "Disponibilité" },
              { value: "14+", label: "Villes desservies" },
              { value: "100%", label: "Clients satisfaits" },
              { value: "6", label: "Services proposés" }
            ].map((stat, i) => (
              <div key={i}>
                <div className="text-4xl md:text-5xl font-bold mb-2">{stat.value}</div>
                <div className="text-white/80">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Zone d'intervention */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="badge-primary mb-4">Proximité</Badge>
            <h2 className="text-3xl font-bold text-[hsl(215,25%,15%)] mb-4">
              Notre zone d'intervention
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Basés à Villiers-sur-Marne, nous intervenons dans tout le Val-de-Marne 
              et les communes limitrophes.
            </p>
            <div className="section-divider" />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4 max-w-5xl mx-auto">
            {cities.map((city) => (
              <Link key={city.id} to={`/villes/${city.id}`}>
                <div className={`city-card text-center ${
                  city.isPrimary ? 'bg-[hsl(207,78%,95%)] border-[hsl(207,78%,42%)]' : ''
                }`}>
                  <MapPin className={`w-5 h-5 mx-auto mb-2 ${
                    city.isPrimary ? 'text-[hsl(207,78%,42%)]' : 'text-gray-400'
                  }`} />
                  <p className={`font-medium text-sm ${
                    city.isPrimary ? 'text-[hsl(207,78%,42%)]' : 'text-[hsl(215,25%,15%)]'
                  }`}>
                    {city.name}
                  </p>
                  {city.isPrimary && (
                    <span className="text-xs text-[hsl(24,95%,53%)]">(Siège)</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[hsl(215,25%,15%)]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Prêt à travailler avec nous ?
          </h2>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
            Contactez NGL SERVICES pour un devis gratuit ou pour toute demande d'information.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href={`tel:${companyInfo.phoneEmergency}`}>
              <Button size="lg" className="emergency-btn rounded-full">
                <Phone className="w-5 h-5 mr-2" />
                {companyInfo.phoneEmergency}
              </Button>
            </a>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-[hsl(207,78%,35%)] hover:bg-gray-100 rounded-full">
                Demander un devis gratuit
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
